import { IChartSettingVersion, IPersistState, IResultState } from './types';
import { CHART_TYPES } from '../../../components/Chart/constants/chartTypes';
import {
  AnalysisKind,
  FunnelModeKey,
  Maybe,
  QueryType,
  TimeUnit,
} from '../../../utils/apollo/graphqlSchema';
import {
  ChartSortEnum,
  ExpressionSettingsLineType,
  IChartSettings,
  IDisplayedMeasureSetting,
  IDisplayedSliceSetting,
  ILegacyChartSettings,
  ISliceFieldsFragment,
  IYAxisSettings,
  ChartNumberType,
  YAxisPosition,
  IAxisSettings,
} from '../../../components/Chart/interfaces';
import { IAnalysisResponse } from '../../../utils/apollo/responseTypes';
import { cloneDeep, isArray, isArrayLike, isNil, isObjectLike } from 'lodash';
import produce from 'immer';
import { OVERALL_SLICE_KEY } from '../../../components/Chart/constants/utils';
import { MaybeNil, YAxisCustomizationType } from '../../../utils/interfaces';
import { assertCondition, assertNotNil, isNotNil } from '../../../utils/types';
import { getRowHeaderKeys, singleDayAllValue } from '../../../utils/builder';
import { searchObjectByKey } from '../../../utils/mapper/utils';
import { COMPARISON_KEY_SUFFIX } from '../../../config/constants';
import { UnreachableCaseError } from 'ts-essentials';
import { defaultExpressionSettings } from '../chartSettings/expression';
import {
  ChartViewOptions,
  initialChartSettings,
  mapDefaultChartKindToChartType,
} from '../chartSettings';
import { theme } from '../../../styles/theme';
import { mainBlueCustomColor } from '../../../components/Chart/utils/analysisColorConfig/customColors';

/**
 * Increment the version if you need to make incompatible changes in the persisted state, that require migration
 * during loading
 */
export const CURRENT_PERSIST_STATE_VERSION = 4;

export const VERSION_HISTORY = {
  V_00_LegacyChartSetting: {
    minVersion: 0,
    description: 'Legacy chart settings',
  } as IChartSettingVersion,
  V_01_LegacyChartSetting: {
    minVersion: 1,
    description: 'Expression Overall, but never persisted as version 1',
  } as IChartSettingVersion,
  V_02_FunnelOverall: {
    minVersion: 2,
    description:
      '2022-11-24: Funnel Overall (KB-3960), introduces versioning of persisted state',
  } as IChartSettingVersion,
  V_03_RetentionOverall: {
    minVersion: 3,
    description: '2022-12-04: Retention Overall (KB-4147)',
  } as IChartSettingVersion,
  V_04_PathOverall: {
    minVersion: 4,
    description: '2023-04-11: Path Overall (KB-4123)',
  } as IChartSettingVersion,
} as const;

/**
 * Check if the argument chartTypeString is a valid {@link CHART_TYPES}
 * @param chartTypeString
 */
export const foundNonUsedChartType = (chartTypeString: Maybe<string>) => {
  if (!chartTypeString) {
    return false;
  }
  // cast to keyof typeof CHART_TYPES to ensure that the string is a valid key
  const testChartType = chartTypeString as keyof typeof CHART_TYPES;
  return !Object.values(CHART_TYPES).some(
    (currChartType) => currChartType === testChartType,
  );
};

/**
 * Return the version of the persisted state. Returns 0 if the state is created prior versioning (i.e.) there is no
 * `version` field
 */
export const getVersionFromChartPref = (chartPref: any): number => {
  return isNil(chartPref.version) || typeof chartPref.version !== 'number'
    ? 0
    : chartPref.version;
};

export const migrateMeasures = (
  chartPref: any,
  defaultMeasureSettings: IDisplayedMeasureSetting[],
  analysis: IAnalysisResponse,
): IDisplayedMeasureSetting[] =>
  produce(defaultMeasureSettings, (draftMeasureSettings) => {
    if (analysis.__typename === 'Funnel') {
      // There is visibleState: boolean[] of each button, indexed by measure. It is very useful for migration.
      // Unlike `funnelLines` which in case of breakdown, contain slice keys (not measure keys) as `name`
      const visibleState = chartPref.visibleState;
      if (isArray(visibleState)) {
        draftMeasureSettings.forEach((measureSetting, i) => {
          if (i < visibleState.length && typeof visibleState[i] === 'boolean') {
            measureSetting.isVisible = visibleState[i];
          }
        });
      }
    }
    // since we had so many changes on measure, therefore we cannot migrate old chart pref to new chart settings
    // then I decided to use default measure setting instead.
    return draftMeasureSettings;
  });

export const migrateSlices = (
  chartPref: any,
  defaultSliceSettings: IDisplayedSliceSetting[],
): IDisplayedSliceSetting[] => {
  const migratedSliceSettings: IDisplayedSliceSetting[] = [];
  // parse slices
  if (chartPref.analysisKind === AnalysisKind.Groupby) {
    const { slicePickers } = chartPref;

    // push slice settings to displayedSliceSettings
    for (const slicePicker of slicePickers) {
      migratedSliceSettings.push({
        isVisible: slicePicker.isVisibleOnChart,
        key: slicePicker.selectedSlice.key,
      });
    }
    // if the size of displayedSliceSettings is not equal to MAX_COUNT,
    // prepend overall to the first one if overall is true
    if (
      defaultSliceSettings[0].key === OVERALL_SLICE_KEY &&
      migratedSliceSettings.length < defaultSliceSettings.length
    ) {
      migratedSliceSettings.unshift({
        isVisible: true,
        key: OVERALL_SLICE_KEY,
      });
    }
  } else {
    migratedSliceSettings.push({ key: OVERALL_SLICE_KEY, isVisible: true });
  }
  return migratedSliceSettings;
};

export const migrateSlicesPath = (
  chartPref: any,
  defaultSliceSettings: IDisplayedSliceSetting[],
  isBreakdownOverall: boolean,
): IDisplayedSliceSetting[] => {
  if (
    isArrayLike(chartPref?.chart?.slices) &&
    isArrayLike(chartPref?.chart?.displayLegends) &&
    chartPref.analysisKind === AnalysisKind.Groupby
  ) {
    const migratedSliceSettings: IDisplayedSliceSetting[] = [];

    const { slices, displayLegends } = chartPref.chart;

    let atLeastOneBreakdownSliceVisible = false;
    for (
      let i = 0;
      i < Math.min(displayLegends.length, defaultSliceSettings.length);
      i++
    ) {
      const displayLegendIndex = displayLegends[i];
      const oldSlice = slices[displayLegendIndex];
      if (isObjectLike(oldSlice)) {
        migratedSliceSettings.push({
          isVisible: oldSlice.isVisibleOnChart,
          key: oldSlice.key,
        });
        atLeastOneBreakdownSliceVisible ||= oldSlice.isVisibleOnChart;
      }
    }

    if (
      isBreakdownOverall &&
      (!atLeastOneBreakdownSliceVisible ||
        migratedSliceSettings.length < defaultSliceSettings.length)
    ) {
      migratedSliceSettings.unshift({
        isVisible: !atLeastOneBreakdownSliceVisible,
        key: OVERALL_SLICE_KEY,
      });
      // trim the array, ensures we don't add more slices than the
      // default settings
      migratedSliceSettings.splice(defaultSliceSettings.length);
    }
    return migratedSliceSettings;
  }

  return defaultSliceSettings;
};

export const getChartType = (
  chartPref: any,
  defaultChartSettings: IChartSettings,
): CHART_TYPES => {
  const prefVersion = getVersionFromChartPref(chartPref);
  const validChartType = Object.values(CHART_TYPES).includes(
    chartPref?.chartSettings?.chartType,
  );
  let chartType: CHART_TYPES;
  if (
    prefVersion >= VERSION_HISTORY.V_02_FunnelOverall.minVersion &&
    validChartType
  ) {
    const maybeChartType = chartPref?.chartSettings
      ?.chartType as MaybeNil<string>;
    assertCondition(
      typeof maybeChartType === 'string',
      `Invalid chart type: ${JSON.stringify(maybeChartType)}`,
    );
    chartType = maybeChartType as CHART_TYPES;
  } else {
    // this is special case for < V_02: the chart type is persisted either in chart settings (after Query Overall)
    // or in the state itself (prior Query Overall)
    chartType =
      chartPref?.chartSettings?.chartType ??
      chartPref?.chartType ??
      defaultChartSettings.chartType;
  }
  assertCondition(validChartType, `Invalid chart type: ${chartType}`);
  return chartType as CHART_TYPES;
};

export const migrateOldExpressionLineTypes = (
  parsedState: any,
  expressionLineType: string,
) => {
  const result = defaultExpressionSettings;

  if (expressionLineType === 'MEASURES_MA') {
    result.lineType = ExpressionSettingsLineType.Ma;
  } else if (expressionLineType === 'MA_ONLY') {
    result.showMeasure = false;
    result.lineType = ExpressionSettingsLineType.Ma;
  } else if (expressionLineType === 'CUMULATIVE_ONLY') {
    result.showMeasure = false;
    result.lineType = ExpressionSettingsLineType.CumulativeSum;
  }

  parsedState.chartSettings.expressionSettings = result;
  delete parsedState.chartSettings.expressionLineType;
};

/**
 * Parse old chartPref to new chartSettings by cherry pick fields from old chartPref
 * @param chartPref old implementation of chart pref which stores most of data
 * @param defaultChartSettings default displayed measure settings for chart, it helps to compare with old chart pref
 * @param analysis the loaded analysis
 * @returns new chart settings from chartPref
 */
export const parseOldChartPrefToChartSettings = (
  chartPref: any,
  defaultChartSettings: IChartSettings,
  analysis: IAnalysisResponse,
): IChartSettings | null => {
  const {
    displayedMeasureSettings: defaultMeasureSettings,
    displayedSliceSettings: defaultSliceSettings,
  } = defaultChartSettings;

  const displayedMeasureSettings = migrateMeasures(
    chartPref,
    defaultMeasureSettings,
    analysis,
  );

  let displayedSliceSettings: IDisplayedSliceSetting[];
  if (analysis.__typename === 'Path') {
    displayedSliceSettings = migrateSlicesPath(
      chartPref,
      defaultSliceSettings,
      analysis.query.breakdown?.overall ?? true,
    );
  } else {
    displayedSliceSettings = migrateSlices(chartPref, defaultSliceSettings);
  }
  // parse format customization to current default measure settings
  if (chartPref.formatCustomization) {
    for (const measureKey of Object.keys(chartPref.formatCustomization)) {
      const currMeasureSetting = displayedMeasureSettings.find(
        (measureSetting) => measureSetting.key === measureKey,
      );
      if (currMeasureSetting) {
        currMeasureSetting.format = {
          ...currMeasureSetting.format,
          ...chartPref.formatCustomization[measureKey],
        };
      }
    }
  }

  // retrieve and test single date label in old chart pref
  let singleDayLabel = '';
  if (
    chartPref.selectedRetentionDate ||
    (chartPref.isSingleDayMode && chartPref.singleDayData?.activeLabel)
  ) {
    if (analysis.__typename === 'Retention') {
      singleDayLabel =
        chartPref.selectedRetentionDate === 'All'
          ? ''
          : chartPref.selectedRetentionDate;
    } else {
      singleDayLabel = chartPref.singleDayData.activeLabel;
    }
  }

  let isConversionView: IChartSettings['isConversionView'] = true;
  if (chartPref.hasOwnProperty('isConversionView')) {
    isConversionView = !!chartPref.isConversionView;
  }

  // retention chart settings
  let isChurn: IChartSettings['isChurn'] = false;
  if (chartPref.hasOwnProperty('isChurn')) {
    isChurn = chartPref.isChurn;
  }

  let chartNumberType: IChartSettings['chartNumberType'] =
    ChartNumberType.Number;
  if (chartPref.hasOwnProperty('selectedMeasure')) {
    chartNumberType =
      chartPref.selectedMeasure === 'number'
        ? ChartNumberType.Number
        : ChartNumberType.Percentage;
  }

  let sort: IChartSettings['sort'] = ChartSortEnum.NONE;
  if (chartPref.hasOwnProperty('sort')) {
    if (analysis.__typename === 'Retention') {
      sort =
        chartPref.sort === 'ascending' || chartPref.sort === ChartSortEnum.ASC
          ? ChartSortEnum.ASC
          : ChartSortEnum.DESC;
    } else {
      sort =
        chartPref.sort === 'ascending' || chartPref.sort === ChartSortEnum.ASC
          ? ChartSortEnum.ASC
          : chartPref.sort === 'descending' ||
              chartPref.sort === ChartSortEnum.DESC
            ? ChartSortEnum.DESC
            : ChartSortEnum.NONE;
    }
  }

  let displaySeq: IChartSettings['displaySeq'] =
    defaultChartSettings.displaySeq;
  if (typeof chartPref?.chart?.displaySeq === 'number') {
    displaySeq = chartPref.chart.displaySeq;
  }

  const chartSettings: IChartSettings = {
    ...initialChartSettings,
    chartType: getChartType(chartPref, defaultChartSettings),
    displayedMeasureSettings,
    displayedSliceSettings,
    singleDayLabel,
    isConversionView,
    isChurn,
    sort,
    chartNumberType,
    displaySeq,
    yAxisSettings: parseYAxisSettings(
      chartPref,
      defaultChartSettings.yAxisSettings,
    ),
  };
  return chartSettings;
};

// validate slices settings, if it's not in the Analysis.slices, fall back to default
export const isSliceSettingValid = (
  displayedSliceSettings: IDisplayedSliceSetting[],
  slices: ISliceFieldsFragment[],
): boolean => {
  for (const sliceSetting of displayedSliceSettings) {
    // overall is the special case since it's not in the slices for sure.
    // XXX note that any OVERALL_COMPARISON_SLICE_KEY *is* actually in the merged slices
    if (
      !slices.find((slice) => slice.key === sliceSetting.key) &&
      sliceSetting.key !== OVERALL_SLICE_KEY
    ) {
      return false;
    }
  }

  return true;
};

// for any displayed measure settings, if there is any key not in the defaultMeasureSettings, return false
const isMeasureSettingValid = (
  displayedMeasureSettings: IDisplayedMeasureSetting[],
  defaultMeasureSettings: IDisplayedMeasureSetting[],
): boolean => {
  for (const measureSetting of displayedMeasureSettings) {
    if (!defaultMeasureSettings.find((m) => m.key === measureSetting.key)) {
      return false;
    }
  }
  return true;
};

// Using [K in QueryType] to ensure all QueryType values are covered
// this Record is used to check the selected chart type is valid for the Analysis.
const availableChartTypesBaseOnQueryType: {
  [K in QueryType]: CHART_TYPES[];
} = {
  [QueryType.Expression]: [
    CHART_TYPES.LINE_CHART,
    CHART_TYPES.BAR_CHART,
    CHART_TYPES.PERCENT_STACKED_AREA_CHART,
    CHART_TYPES.STACKED_COLUMN_CHART,
    CHART_TYPES.METRIC_CHART,
    CHART_TYPES.GEO_MAP,
    CHART_TYPES.PIE_CHART,
    CHART_TYPES.EXPRESSION_TABLE,
  ],
  [QueryType.Funnel]: [
    CHART_TYPES.FUNNEL_CHART,
    CHART_TYPES.COMPARATIVE_LINE_CHART,
  ],
  [QueryType.Retention]: [
    CHART_TYPES.RETENTION,
    CHART_TYPES.RETENTION_LINE,
    CHART_TYPES.RETENTION_BAR,
  ],
  [QueryType.Path]: [CHART_TYPES.PATH],
  [QueryType.CohortSample]: [CHART_TYPES.COHORT_SAMPLE],
  [QueryType.DataTable]: [CHART_TYPES.DATA_TABLE],
  [QueryType.Significance]: [CHART_TYPES.SIGNIFICANCE],
  [QueryType.Prediction]: [CHART_TYPES.PREDICTION],
};

const isValidChartType = (chartType: CHART_TYPES, queryType: QueryType) => {
  const availableChartTypes = availableChartTypesBaseOnQueryType[queryType];
  return availableChartTypes.includes(chartType);
};

/**
 * validate the result current state with analysis data,
 * if we cannot persist states, then return null to fallback to default
 * @param result current parsed states
 * @param analysis raw data from Backend
 * @param defaultChartSettings
 * @returns validated result or null
 */
export const validateResultFields = (
  currentChartSettings: IChartSettings,
  analysis: IAnalysisResponse,
  defaultChartSettings: IChartSettings,
): IChartSettings | null => {
  const {
    singleDayLabel,
    displayedSliceSettings,
    displayedMeasureSettings,
    chartType,
  } = currentChartSettings;

  if (!isValidChartType(chartType, analysis.queryType)) {
    return null;
  }

  if (
    analysis.__typename === 'Expression' ||
    analysis.__typename === 'Funnel' ||
    analysis.__typename === 'DataTable'
  ) {
    // KB-7887: if timeUnit is allTime, the label is the range, but not the single day.
    // therefore we display all date on the date range, no need to validate if it's in the data.
    assertNotNil(analysis.data);
    if (singleDayLabel.length > 0 && analysis.timeUnit !== TimeUnit.AllTime) {
      const dates = analysis.data.dates;
      if (!dates.find((date) => date === singleDayLabel)) {
        return null;
      }
    }

    // for any case, if displayedMeasureSettings length not match with
    // default displayedMeasureSettings, fallback to default
    // edge case on geo map at the moment, but feasible for all types of charts
    if (
      displayedMeasureSettings.length !==
      defaultChartSettings.displayedMeasureSettings.length
    ) {
      return null;
    }

    if (
      !isSliceSettingValid(displayedSliceSettings, analysis.data.slices) ||
      !isMeasureSettingValid(
        displayedMeasureSettings,
        defaultChartSettings.displayedMeasureSettings,
      )
    ) {
      return null;
    }

    // in metric chart, if single day label is not set, we use the last date as single day label for legacy charts.
    if (chartType === CHART_TYPES.METRIC_CHART && !singleDayLabel) {
      currentChartSettings.singleDayLabel =
        analysis.data.dates[analysis.data.dates.length - 1];
    }

    // if both current and default single date label are set, and it's all time.
    // we should always use the default single day label.
    if (
      analysis.timeUnit === TimeUnit.AllTime &&
      defaultChartSettings.singleDayLabel &&
      currentChartSettings.singleDayLabel &&
      defaultChartSettings.singleDayLabel !==
        currentChartSettings.singleDayLabel
    ) {
      currentChartSettings.singleDayLabel = defaultChartSettings.singleDayLabel;
    }
  } else if (analysis.__typename === 'Retention') {
    assertNotNil(analysis.data);
    if (!isSliceSettingValid(displayedSliceSettings, analysis.data.slices)) {
      return null;
    }

    // Single day label is derived from the row headers (the values in the first column
    // of the retention table). If there is such row, preserve it, otherwise - ignore it.
    // It was permanently ignored since KB-6330 but restored since KBT-35
    const validatedSingleDayLabel = getRowHeaderKeys(analysis.data).includes(
      singleDayLabel,
    )
      ? singleDayLabel
      : '';
    // compare the measure keys, if not match, fallback to default
    const measureKeys = displayedMeasureSettings.map((m) => m.key);
    const defaultMeasureKeys =
      defaultChartSettings.displayedMeasureSettings.map((m) => m.key);
    const useCurrMeasureSettings =
      measureKeys.length === defaultMeasureKeys.length &&
      defaultMeasureKeys.every((key) => measureKeys.includes(key));
    return {
      ...currentChartSettings,
      singleDayLabel: validatedSingleDayLabel,
      displayedMeasureSettings: useCurrMeasureSettings
        ? displayedMeasureSettings
        : defaultChartSettings.displayedMeasureSettings,
    };
  } else if (analysis.__typename === 'Path') {
    let slices: ISliceFieldsFragment[];
    assertNotNil(analysis.data);
    const modeValue = analysis.data.modeValue;
    switch (modeValue.__typename) {
      case 'SankeyPathModeValue':
        slices = modeValue.flows;
        break;
      case 'TopPathsPathModeValue':
        slices = modeValue.slices;
        break;
      default:
        throw new UnreachableCaseError(modeValue);
    }
    if (!isSliceSettingValid(displayedSliceSettings, slices)) {
      return null;
    }
  }

  // this is a fix for the legacy chart pref which saved the color variable name in chart settings.
  displayedMeasureSettings.forEach((measure) => {
    if (measure.color === theme.colors.mainBlue) {
      measure.color = mainBlueCustomColor.hex;
    }
  });

  displayedSliceSettings.forEach((slice) => {
    if (slice.color === theme.colors.mainBlue) {
      slice.color = mainBlueCustomColor.hex;
    }
  });

  return currentChartSettings;
};

// remove after we drop all old chart pref
export const deleteLegacyFields = (chartPref: any) => {
  // special treatment for now, will remove it after apply all chart setting V2
  if (chartPref.startDate && chartPref.endDate) {
    delete chartPref.startDate;
    delete chartPref.endDate;
  }
  delete chartPref.dualAxesMode;
  delete chartPref.isCombineDualAxes;
  delete chartPref.permissions;
  if (chartPref.referenceSql) {
    delete chartPref.referenceSql;
  }
};

/**
 * Copy default settings to the parsed result. There might be new values that are introduced to IChartSettings
 * which aren't present in the parsed state of IChartSettings.
 *
 * CurrentChartSettings is a casted value from Json object,
 * so we need to use hasOwnProperty to check if the key exists.
 * @param currentChartSettings - parsed chart settings V2
 * @param defaultChartSettings - current default settings
 */
export const applyDefaultChartSettings = (
  currentChartSettings: IChartSettings,
  defaultChartSettings: IChartSettings,
) => {
  if (typeof currentChartSettings !== 'object') {
    return;
  }
  Object.entries(defaultChartSettings).forEach(([k, v]) => {
    if (!currentChartSettings.hasOwnProperty(k)) {
      // there is no other way to treat chart settings as object in order to assign the defaults
      // @ts-expect-error : ts7053: Element implicitly has an 'any' type because expression
      // of type 'string' can't be used to index type 'IChartSettings'.
      currentChartSettings[k] = cloneDeep(v);
    }
  });
};

export const areChartSettingsMigrated = (
  parsedState: any,
  queryType: QueryType,
): parsedState is IResultState => {
  if (isObjectLike(parsedState?.chartSettings)) {
    // post Chart Settings V2
    const prefVersion = getVersionFromChartPref(parsedState);
    switch (queryType) {
      case QueryType.Expression: {
        return true;
      }
      case QueryType.Funnel: {
        // prior introduction of state versioning there Expression settings are persisted for non-Expression settings
        // By using persisted state version, we can determine if they are migrated
        if (prefVersion >= VERSION_HISTORY.V_02_FunnelOverall.minVersion) {
          return true;
        } else {
          return false;
        }
      }
      case QueryType.Retention:
        if (prefVersion >= VERSION_HISTORY.V_03_RetentionOverall.minVersion) {
          return true;
        }
        return false;
      case QueryType.Path:
        return prefVersion >= VERSION_HISTORY.V_04_PathOverall.minVersion;
      case QueryType.CohortSample:
      case QueryType.DataTable:
      case QueryType.Significance:
        return true;
      case QueryType.Prediction:
        return false;
      default:
        throw new UnreachableCaseError(queryType);
    }
  } else {
    // pre Chart Settings V2
    return false;
  }
};

/**
 * @Brett, Not pretty, but it works.
 * The reason was I moved state.sort into chartSettings.sort, but it didn't trigger the migrate checking
 * because we already have a chartSettings object in the state.
 * Therefore I need to move the parsedState.sort manually into chartSettings.
 *
 * In the future, we will move more fields(Y-axis config, events, etc) into chartSetting
 * and append more under this function.
 */
export const adjustPrefToMatchChartSettings = (
  parsedState: any,
  defaultChartSettings: IChartSettings,
) => {
  if (parsedState.sort && !parsedState.chartSettings.sort) {
    parsedState.chartSettings.sort = parsedState.sort;
  }

  if (!isObjectLike(parsedState?.chartSettings?.yAxisSettings)) {
    parsedState.chartSettings.yAxisSettings = parseYAxisSettings(
      parsedState,
      defaultChartSettings.yAxisSettings,
    );
  }

  // KB-5396 - rename event to incident
  if (parsedState.activeEventTypes && !parsedState.activeIncidentTypes) {
    parsedState.activeIncidentTypes = parsedState.activeEventTypes;
  }
  delete parsedState.activeEventTypes;

  // on KBT-521
  // update type in IGeoMapConfig, rename translateX, translateY, scale to x, y, k
  // if scale exists, we need to migrate it since scale is always larger than 0
  if (parsedState.chartSettings?.geoMapConfig?.scale) {
    parsedState.chartSettings.geoMapConfig.x =
      parsedState.chartSettings.geoMapConfig.translateX;
    parsedState.chartSettings.geoMapConfig.y =
      parsedState.chartSettings.geoMapConfig.translateY;
    parsedState.chartSettings.geoMapConfig.k =
      parsedState.chartSettings.geoMapConfig.scale;
    delete parsedState.chartSettings.geoMapConfig.translateX;
    delete parsedState.chartSettings.geoMapConfig.translateY;
    delete parsedState.chartSettings.geoMapConfig.scale;
  }

  // on KBT-672, we removed the chart view on funnelDateSelection, and use expanded bar chart instead.
  // we need to migrate the funnelDateSelection to singleDayLabel
  if (
    parsedState.chartSettings?.funnelDateSelection &&
    parsedState.chartSettings?.funnelDateSelection !== singleDayAllValue
  ) {
    parsedState.chartSettings.singleDayLabel =
      parsedState.chartSettings.funnelDateSelection;
  }
  delete parsedState.chartSettings.funnelDateSelection;

  // KBT-946, rename retentionNumberFormat to chartNumberType
  // and expand the usage to Stacked Column Chart / Area Chart
  if (parsedState.chartSettings.retentionNumberFormat) {
    parsedState.chartSettings.chartNumberType =
      parsedState.chartSettings.retentionNumberFormat;
    delete parsedState.chartSettings.retentionNumberFormat;
  }
  return;
};

type IChartSettingsWithLegacy = IChartSettings & ILegacyChartSettings;

/**
 * Migration for global to per-measure YAxis config:
 * KB-5674 Dual-Axis Control Improvements
 *
 */
export const migrateDualAxesConfig = (
  chartSettingsWithLegacy: IChartSettingsWithLegacy,
  defaultChartSettings: IChartSettings,
) => {
  const { displayedMeasureSettings } = chartSettingsWithLegacy;
  const { displayedMeasureSettings: defaultMeasureSettings } =
    defaultChartSettings;

  // populate yAxisPosition from default settings
  for (const measure of displayedMeasureSettings) {
    if (isNil(measure.yAxisPosition)) {
      const defaultMeasure = defaultMeasureSettings.find(
        (m) => m.key === measure.key,
      );
      measure.yAxisPosition =
        defaultMeasure?.yAxisPosition ?? YAxisPosition.LEFT;
    }
  }

  // migrate legacy dualAxesConfig to per measure yAxisPosition
  const legacyChartSettings = chartSettingsWithLegacy as ILegacyChartSettings;
  const { dualAxesConfig } = legacyChartSettings;

  // drop the legacy dualAxesConfig
  delete legacyChartSettings.dualAxesConfig;

  // defensive check
  if (!isArrayLike(dualAxesConfig)) {
    return;
  }

  // Dual Axis config only triggers if there are just 2 visible measures
  const visibleMeasuresCount = displayedMeasureSettings.reduce(
    (sum, m) => sum + (m.isVisible ? 1 : 0),
    0,
  );

  if (visibleMeasuresCount === 2) {
    let dualAxesConfigIndex = 0;
    for (const measure of displayedMeasureSettings) {
      if (measure.isVisible) {
        if (dualAxesConfigIndex < dualAxesConfig.length) {
          measure.yAxisPosition = dualAxesConfig[dualAxesConfigIndex];
          dualAxesConfigIndex++;
        } else {
          break;
        }
      }
    }
  }
};

/**
 * Convert legacy state for Y-Axis customizations to new chart settings
 *
 * @param persistState
 * @param defaultYAxisSettings
 */
export const parseYAxisSettings = (
  persistState: Pick<IPersistState, 'isTruncateYAxis' | 'yAxisCustomization'>,
  defaultYAxisSettings: IYAxisSettings,
): IYAxisSettings => {
  const yAxisSettings = cloneDeep(defaultYAxisSettings);
  if (typeof persistState.isTruncateYAxis === 'boolean') {
    yAxisSettings[YAxisPosition.LEFT].isTruncateYAxis =
      persistState.isTruncateYAxis;
    yAxisSettings[YAxisPosition.RIGHT].isTruncateYAxis =
      persistState.isTruncateYAxis;
  }
  if (
    isObjectLike(persistState.yAxisCustomization) &&
    persistState?.yAxisCustomization?.type === YAxisCustomizationType.LINEAR
  ) {
    yAxisSettings[YAxisPosition.LEFT].yAxisCustomization =
      persistState.yAxisCustomization;
    yAxisSettings[YAxisPosition.RIGHT].yAxisCustomization =
      persistState.yAxisCustomization;
  }
  delete persistState.isTruncateYAxis;
  delete persistState.yAxisCustomization;

  return yAxisSettings;
};

/**
 * Integration point for migrating existing (persisted) chart settings
 * to the current IChartSettings.
 * Also including the migration patch for each feature and bugfixes
 *
 * @param analysis
 * @param parsedState
 * @param defaultChartSettings
 */
export const migrateChartSettingsV2 = (
  analysis: IAnalysisResponse,
  currentChartSettings: IChartSettings,
  defaultChartSettings: IChartSettings,
) => {
  // need to check if axisSettings is already in currentChartSettings
  // before applying default settings, and if so, migrate IYAxisSettings to IAxisSettings.
  let isCurrentChartSettingHasAxisSettings =
    !!currentChartSettings.axisSettings;
  applyDefaultChartSettings(currentChartSettings, defaultChartSettings);
  const chartSettingsWithLegacy =
    currentChartSettings as IChartSettingsWithLegacy;
  if (analysis.queryType === QueryType.Expression) {
    migrateDualAxesConfig(chartSettingsWithLegacy, defaultChartSettings);
    searchObjectByKey(currentChartSettings, 'name', updateNameOnCumulativeLine);
    searchObjectByKey(
      currentChartSettings,
      'display',
      updateNameOnCumulativeLine,
    );

    assertCondition(analysis.__typename === 'Expression');
    const isReportInUrl = window.location.pathname.includes('report');
    if (isReportInUrl) {
      // if report is in url, and the chartType is EXPRESSION_TABLE
      // this option does not exist in the chartTypeOptions for reports.
      // convert to default chart type
      if (currentChartSettings.chartType === CHART_TYPES.EXPRESSION_TABLE) {
        const defaultChartType = mapDefaultChartKindToChartType(
          analysis.chartControl.default,
        );
        currentChartSettings.chartType = defaultChartType;
      }
    } else if (!isReportInUrl) {
      // if in dashboard or workspace, and the chart view is table, use EXPRESSION_TABLE
      if (
        currentChartSettings.tableSettings.chartView === ChartViewOptions.Table
      ) {
        currentChartSettings.chartType = CHART_TYPES.EXPRESSION_TABLE;
      }
    }

    // on KBT-673, create CHART_TYPES.PIE_CHART to handle previous case:
    // (StackedBar || PerentageArea) + SingleDayLabel = PieChart
    if (
      (currentChartSettings.chartType === CHART_TYPES.STACKED_COLUMN_CHART ||
        currentChartSettings.chartType ===
          CHART_TYPES.PERCENT_STACKED_AREA_CHART) &&
      currentChartSettings.singleDayLabel
    ) {
      currentChartSettings.chartType = CHART_TYPES.PIE_CHART;
    }
  }

  // on KBT-878, if the date range is changed, the default singleDayLabel will be changed for TimeUnit.AllTime.
  // thus we cannot locate the data by persisted singleDayLabel.
  // When parseing the chart settings, we need to set it to default singleDayLabel for these 3 cases.
  // 1. Metric Chart
  // 2. Pie Chart
  // 3. Bar Chart with singleDayLabel
  if (
    (currentChartSettings.chartType === CHART_TYPES.METRIC_CHART ||
      currentChartSettings.chartType === CHART_TYPES.PIE_CHART ||
      (currentChartSettings.chartType === CHART_TYPES.BAR_CHART &&
        currentChartSettings.singleDayLabel)) &&
    analysis.timeUnit === TimeUnit.AllTime
  ) {
    currentChartSettings.singleDayLabel = defaultChartSettings.singleDayLabel;
  }

  if (analysis.queryType === QueryType.Funnel) {
    assertCondition(analysis.__typename === 'Funnel');

    // KBT-363:
    // For legacy Funnel Freq and TimeToConvert
    // if the visible displayed measure is not 2, fallback to default
    // because Freq and TimeToConvert requires 2 visible measures to work
    if (
      [FunnelModeKey.Frequency, FunnelModeKey.TimeToConvert].includes(
        analysis.query.mode?.key as FunnelModeKey,
      )
    ) {
      const visibleDisplayedMeasure =
        currentChartSettings.displayedMeasureSettings.filter(
          (measure: IDisplayedMeasureSetting) => measure.isVisible,
        );
      if (visibleDisplayedMeasure.length !== 2) {
        currentChartSettings.displayedMeasureSettings =
          defaultChartSettings.displayedMeasureSettings;
      }
    }
  }

  // KBT-1184: Central Chart Setting, in this ticket, we added new interface as IAxisSettings
  // and deprecated the old yAxisSettings.
  // in the case, we don't have axisSettings, but we have yAxisSettings, we need to migrate it.
  if (
    currentChartSettings.yAxisSettings &&
    !isCurrentChartSettingHasAxisSettings
  ) {
    const { yAxisSettings } = currentChartSettings;
    const axisSettings: IAxisSettings = cloneDeep(
      initialChartSettings.axisSettings,
    );
    for (const [key, value] of Object.entries(yAxisSettings)) {
      assertCondition(
        key === YAxisPosition.LEFT || key === YAxisPosition.RIGHT,
      );
      axisSettings.yAxisSettings[key] = {
        ...axisSettings.yAxisSettings[key],
        isTruncate: value.isTruncateYAxis ?? false,
      };

      if (
        value.yAxisCustomization?.type === YAxisCustomizationType.LINEAR &&
        isNotNil(value.yAxisCustomization.minValue) &&
        isNotNil(value.yAxisCustomization.maxValue)
      ) {
        axisSettings.yAxisSettings[key as YAxisPosition].bounds = {
          min: value.yAxisCustomization.minValue,
          max: value.yAxisCustomization.maxValue,
        };
      }
    }
    currentChartSettings.axisSettings = axisSettings;
  }
};

// for backward compatible, update `- Cum.` to `- Cumulative Sum`
export const updateNameOnCumulativeLine = (value: Maybe<string>) => {
  if (value?.includes('- Cum.')) {
    return value.replace('- Cum.', '- Cumulative Sum');
  } else {
    return value;
  }
};

export const checkDefaultAndCurrentSliceSettings = (
  parsedState: IResultState,
  defaultChartSettings: IChartSettings,
  analysis: IAnalysisResponse,
) => {
  // validate current slice settings against default slice settings
  const currentDisplayedSliceSettings =
    parsedState.chartSettings.displayedSliceSettings;
  if (
    analysis.__typename !== 'Path' &&
    analysis.__typename !== 'Prediction' &&
    analysis.__typename !== 'CohortSample'
  ) {
    const updatedDisplayedSliceSettings = [
      ...defaultChartSettings.displayedSliceSettings,
    ];

    // using currentChartSetting to update the isVisible and color field.
    // this is to ensure the rank and data is in correct order after report updated.
    // if slice is not found in currentSetting, use default setting on the slice.
    for (const displayedSliceSetting of updatedDisplayedSliceSettings) {
      const currentSliceSetting = currentDisplayedSliceSettings.find(
        (slice) => slice.key === displayedSliceSetting.key,
      );
      if (currentSliceSetting) {
        displayedSliceSetting.isVisible = currentSliceSetting.isVisible;
        displayedSliceSetting.color = currentSliceSetting.color;
      }
    }

    // KBT-840 filter out comparison slices. We don't need to keep track on
    // them
    parsedState.chartSettings.displayedSliceSettings =
      updatedDisplayedSliceSettings.filter(
        (sliceSetting) => !sliceSetting.key.endsWith(COMPARISON_KEY_SUFFIX),
      );
  }
};
